'use client'

import clsx from 'clsx'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import React, { useMemo, useState, useRef, useEffect } from 'react'

import { IAmbulance, IPriceListItem, PRICE_LIST_ITEM_BUTTON_TYPE_ENUM } from '@/_types'
import { pushAddToCart, pushSelectItem } from '@/_utils/analytics/datalayer'

import { formatPrice } from '../../utils/price-list.utils'

interface IProps {
	ambulance: IAmbulance
	priceListItems: IPriceListItem[]
	/** Full ambulance price list for GA4 `index` (same order as `view_item_list`). */
	allPriceListItems: IPriceListItem[]
	allowOrder?: boolean
}

function ASelfPaidPrice({ priceListItem }: { priceListItem: IPriceListItem }) {
	if (!priceListItem) return '-'
	return formatPrice(priceListItem.priceInsurance)
}

function AAmbulanceServicestemOrderButton({
	priceListItem,
	ambulanceSlug,
	ambulanceName,
	ga4Index,
	className
}: {
	priceListItem: IPriceListItem
	ambulanceSlug: string
	ambulanceName: string
	ga4Index: number
	className?: string
}) {
	const t = useTranslations('pages.ambulance_detail')

	const isItemTimeslots = useMemo(() => {
		return priceListItem.buttonType === PRICE_LIST_ITEM_BUTTON_TYPE_ENUM.TIMESLOTS
	}, [priceListItem])

	if (isItemTimeslots) {
		return (
			<Link
				href={`/volne-terminy?ambulance=${ambulanceSlug}`}
				title='Order'
				className={clsx('btn_basic type4 min-w-[217px]', className)}
				prefetch={true}
			>
				<span>{t('termins')}</span>
				<span>
					<span>
						<svg className='sico'>
							<use href='/assets/layout/icons.svg#s_ar2_r'></use>
						</svg>
					</span>
					<span>
						<svg className='sico'>
							<use href='/assets/layout/icons.svg#s_ar2_r'></use>
						</svg>
					</span>
				</span>
			</Link>
		)
	} else {
		if (priceListItem.priceAction !== 'recepcia') {
			const handleOrderClick = () => {
				const listContext = {
					item_list_id: `ambulancia-${ambulanceSlug}`,
					item_list_name: `Ambulancia - ${ambulanceName}`,
					index: ga4Index,
					item_category2: ambulanceName,
					ambulanceSlug
				}
				const cartItem = {
					id: priceListItem.id,
					itemId: priceListItem.id,
					name: priceListItem.name,
					type: 'price-list-item' as const,
					price: priceListItem.price ?? 0,
					priceOld: priceListItem.priceOld ?? undefined
				}
				pushSelectItem(cartItem, listContext)
				pushAddToCart(cartItem, { item_category2: ambulanceName, ambulanceSlug })
			}
			return (
				<Link
					href={`/volne-terminy?type=price-list-item&id=${priceListItem.id}&ambulance=${ambulanceSlug}`}
					title='Order'
					className={clsx('btn_basic min-w-[217px] type4', className)}
					prefetch={true}
					onClick={handleOrderClick}
				>
					<span>{t('order')}</span>
					<span>
						<span>
							<svg className='sico'>
								<use href='/assets/layout/icons.svg#s_ar2_r'></use>
							</svg>
						</span>
						<span>
							<svg className='sico'>
								<use href='/assets/layout/icons.svg#s_ar2_r'></use>
							</svg>
						</span>
					</span>
				</Link>
			)
		}

		return (
			<p className={clsx('text-[13px] pdt-30lg italic', className)}>
				{t('orderableViaReception')}
			</p>
		)
	}
}

/* -------------------- Default Table -------------------- */
export default function AAmbulanceServicesTable({
	ambulance,
	priceListItems,
	allPriceListItems
}: IProps) {
	const [showMore, setShowMore] = useState(false)
	const t = useTranslations('pages.ambulance_detail')
	const headersRef = useRef<HTMLDivElement>(null)
	const contentRef = useRef<HTMLDivElement>(null)

	const unorderablePriceListItems = useMemo(() => {
		return showMore ? priceListItems : priceListItems.slice(0, 4)
	}, [priceListItems, showMore])

	// Synchronize horizontal scrolling between headers and content
	useEffect(() => {
		const headersElement = headersRef.current
		const contentElement = contentRef.current

		if (!headersElement || !contentElement) return

		let isScrolling = false

		const handleHeadersScroll = () => {
			if (isScrolling) return
			isScrolling = true
			contentElement.scrollLeft = headersElement.scrollLeft
			requestAnimationFrame(() => {
				isScrolling = false
			})
		}

		const handleContentScroll = () => {
			if (isScrolling) return
			isScrolling = true
			headersElement.scrollLeft = contentElement.scrollLeft
			requestAnimationFrame(() => {
				isScrolling = false
			})
		}

		headersElement.addEventListener('scroll', handleHeadersScroll, { passive: true })
		contentElement.addEventListener('scroll', handleContentScroll, { passive: true })

		return () => {
			headersElement.removeEventListener('scroll', handleHeadersScroll)
			contentElement.removeEventListener('scroll', handleContentScroll)
		}
	}, [])

	if (!unorderablePriceListItems.length) return null

	return (
		<div id='price-list-table' className='item_pricing-table mgl-0lg mgl-120 mgt-30lg mgt-50'>
			{/* Desktop Layout - Original table with horizontal scroll */}
			<div className='header-filler'></div>
			<div className=''>
				{/* Headers Container - Sticky only on mobile */}
				<div className='sticky lg:static top-[104px] z-[5] bg-[#fdfbf7]'>
					<div
						ref={headersRef}
						className='overflow-x-auto overflow-y-hidden scrollbar-hide'
						style={{ WebkitOverflowScrolling: 'touch' }}
					>
						<table className='w-full table-fixed border-collapse'>
							<thead>
								<tr>
									<th scope='col' className='text-left'>
										{t('priceListName')} {ambulance.name}
									</th>
									<th scope='col' className='text-left'>
										{t('priceInsurance')}
									</th>
									<th scope='col' className='hide_lgm'></th>
								</tr>
							</thead>
						</table>
					</div>
				</div>

				{/* Content Container */}
				<div
					ref={contentRef}
					className='overflow-x-auto scrollbar-hide'
					style={{ WebkitOverflowScrolling: 'touch' }}
				>
					<table className='w-full table-fixed border-collapse'>
						<tbody>
							{unorderablePriceListItems.map((priceListItem: IPriceListItem) => {
								const ga4Index = allPriceListItems.findIndex(i => i.id === priceListItem.id)
								return (
								<tr key={priceListItem.id}>
									<td>
										<div className='dflex flex-dir-column align-start'>
											<div>
												{priceListItem.name}
												{priceListItem.description?.trim()?.length && (
													<span className='text-sm mt-2 block'>
														{priceListItem.description}
													</span>
												)}
											</div>
									<AAmbulanceServicestemOrderButton
											ambulanceSlug={ambulance.slug}
											ambulanceName={ambulance.name}
											priceListItem={priceListItem}
											ga4Index={ga4Index >= 0 ? ga4Index : 0}
											className='hide_lg'
										/>
									</div>
								</td>
								<td className='highlight'>
									<ASelfPaidPrice priceListItem={priceListItem} />
								</td>
								<td className='hide_lgm min-w-[200px]'>
									<AAmbulanceServicestemOrderButton
										ambulanceSlug={ambulance.slug}
										ambulanceName={ambulance.name}
										priceListItem={priceListItem}
										ga4Index={ga4Index >= 0 ? ga4Index : 0}
									/>
									</td>
								</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>

			{priceListItems.length > 4 && (
				<div className='dflex justify-center mgt-10md mgt-40lg mgt-30'>
					<Link
						href='#'
						className='btn_basic type7 down'
						onClick={e => {
							e.preventDefault()
							setShowMore(prev => !prev)
						}}
						prefetch={false}
					>
						<span>{showMore ? t('showLess') : t('showMore')}</span>
						<span>
							<span>
								<svg className='sico'>
									{showMore ? (
										<use href='/assets/layout/icons.svg#s_ar1_u'></use>
									) : (
										<use href='/assets/layout/icons.svg#s_ar1_d'></use>
									)}
								</svg>
							</span>
							<span>
								<svg className='sico'>
									{showMore ? (
										<use href='/assets/layout/icons.svg#s_ar1_u'></use>
									) : (
										<use href='/assets/layout/icons.svg#s_ar1_d'></use>
									)}
								</svg>
							</span>
						</span>
					</Link>
				</div>
			)}
		</div>
	)
}
